import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV = [
  { label: 'Product',      href: '/#product' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'About',        href: '/about' },
  { label: 'Enterprise',   href: '/enterprise' },
];

export default function PublicHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeRef = useRef(null);
  const location = useLocation();

  // Close on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  // Escape key
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen && closeRef.current) closeRef.current.focus();
  }, [menuOpen]);

  return (
    <>
      <header className="pub-header" data-testid="pub-header">
        <div className="pub-container w-full flex items-center justify-between gap-6">
          {/* Wordmark + parent brand */}
          <Link
            to="/"
            className="flex flex-col items-start leading-none shrink-0"
            style={{ textDecoration: 'none', gap: '4px' }}
            aria-label="KYRDROS — home"
            data-testid="pub-wordmark"
          >
            <span className="font-wordmark text-white" style={{ fontSize: '27px', letterSpacing: '-0.01em' }}>
              KYRDROS
            </span>
            <span
              data-testid="header-from-durahuman"
              style={{ display: 'flex', alignItems: 'center', gap: '5px', lineHeight: 1 }}
            >
              <span style={{
                fontFamily: 'IBM Plex Sans, Helvetica, Arial, sans-serif',
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)',
                flexShrink: 0,
              }}>FROM</span>
              <img
                src="/images/durahuman-wordmark-transparent.png"
                alt="DURAHUMAN"
                style={{ height: '12px', width: 'auto', opacity: 0.95, display: 'block', flexShrink: 0 }}
                draggable="false"
              />
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7" aria-label="Public navigation">
            {NAV.map(n => (
              <a key={n.label} href={n.href} className="pub-nav-link" data-testid={`pub-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}>
                {n.label}
              </a>
            ))}
          </nav>

          {/* Desktop right actions */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link to="/login" className="pub-nav-link" style={{ color: '#fff' }} data-testid="pub-login-link">
              Login
            </Link>
            <Link to="/enterprise" className="pub-btn-primary" style={{ minHeight: '38px', padding: '0 18px', fontSize: '13px' }} data-testid="pub-request-access-btn">
              Request Access
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden flex items-center justify-center"
            style={{ minHeight: '44px', minWidth: '44px', background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}
            onClick={() => setMenuOpen(true)}
            aria-label="Open navigation"
            aria-expanded={menuOpen}
            data-testid="pub-mobile-menu-btn"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        className={`pub-mobile-overlay ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile nav drawer */}
      <nav
        className={`pub-mobile-nav ${menuOpen ? 'open' : ''}`}
        aria-label="Mobile navigation"
        data-testid="pub-mobile-nav"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #d9dee6' }}>
          <div className="flex flex-col items-start leading-none" style={{ gap: '4px' }}>
            <span className="font-wordmark text-black" style={{ fontSize: '24px' }}>
              KYRDROS
            </span>
            <span
              style={{ display: 'flex', alignItems: 'center', gap: '4px', lineHeight: 1 }}
            >
              <span style={{
                fontFamily: 'IBM Plex Sans, Helvetica, Arial, sans-serif',
                fontSize: '8px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#8a94a6',
                flexShrink: 0,
              }}>FROM</span>
              <img
                src="/images/durahuman-wordmark-transparent.png"
                alt="DURAHUMAN"
                style={{ height: '11px', width: 'auto', opacity: 0.82, display: 'block', flexShrink: 0 }}
                draggable="false"
              />
            </span>
          </div>
          <button
            ref={closeRef}
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation"
            style={{ minHeight: '44px', minWidth: '44px', background: 'none', border: 'none', cursor: 'pointer', color: '#667085', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="pub-mobile-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav links */}
        <div className="flex-1 px-5 py-6 space-y-1">
          {NAV.map(n => (
            <a
              key={n.label}
              href={n.href}
              onClick={() => setMenuOpen(false)}
              className="flex items-center"
              style={{ minHeight: '48px', fontFamily: 'IBM Plex Sans, Helvetica, Arial, sans-serif', fontSize: '16px', fontWeight: '500', color: '#000', textDecoration: 'none', borderBottom: '1px solid #f0f2f5', paddingBottom: '12px' }}
              data-testid={`pub-mobile-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
            >
              {n.label}
            </a>
          ))}
        </div>

        {/* Mobile CTA */}
        <div className="px-5 pb-8 space-y-3">
          <Link
            to="/login"
            onClick={() => setMenuOpen(false)}
            className="pub-btn-ghost"
            style={{ width: '100%', justifyContent: 'center', fontSize: '15px' }}
            data-testid="pub-mobile-login-btn"
          >
            Login
          </Link>
          <Link
            to="/enterprise"
            onClick={() => setMenuOpen(false)}
            className="pub-btn-primary"
            style={{ width: '100%', justifyContent: 'center', fontSize: '15px' }}
            data-testid="pub-mobile-request-btn"
          >
            Request Access
          </Link>
        </div>
      </nav>
    </>
  );
}
